import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { MultifactorAuthState } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import router from '../../../router/index';
import { AppContext } from '../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../translator';
import ScreenUrl from '../../../router/url';

export const actions: ActionTree<MultifactorAuthState, RootState> = {
    /**
     * 多要素認証デバイス一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} word 検索キーワード
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { word = '', pageNumber, perPage, sort } = {}) {
        const payload = {
            word: word,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.MultifactorAuth.SEARCH, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.SEARCH_FAILURE, params);
        }
    },

    /**
     * 多要素認証デバイス(ハードウェアトークン)作成前のエラー等のクリア
     * 作成に失敗していた場合などに、そのままエラーメッセージなどVuex上に残っていたらクリアするため
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async new({ commit }) {
        commit(types.NEW_HARD_DEVICES);
    },

    /**
     * 多要素認証デバイス(ハードウェアトークン)新規作成
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {HardDevice} hardDevice 新規情報
     * @returns {Promise<void>}
     */
    async hard({ dispatch, commit, rootState }, hard_device) {
        try {
            commit(types.CREATE_HARD_DEVICES_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.MultifactorAuth.HARD, params: hard_device },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_HARD_DEVICES_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.MultifactorAuth.ADD_HARD);
                dispatch('toastMessage', `${message}`, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_HARD_DEVICES_FAILURE, params);
        }
    },

    /**
     * 多要素認証デバイス詳細情報取得
     * @param {any} dispatch VueX変数
     * @param {any} commit VueX変数
     * @param {number} id デバイスID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.MultifactorAuth.SHOW.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * デバイスの多要素認証の有効/無効を更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} deviceIds 更新するデバイスのIDの配列
     * @param {any} enabled 多要素認証が有効/無効
     * @returns {Promise<void>}
     */
    async switchDevices({ dispatch, commit, rootState }, { deviceIds, enabled } = {}) {
        const payload = {
            device_ids: deviceIds,
            enabled: enabled,
        };
        try {
            commit(types.SWITCH_DEVICES_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'put', url: ApiUrl.MultifactorAuth.SWITCH_DEVICES, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SWITCH_DEVICES_SUCCESS, resData);
                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.MultifactorAuth.CHANGED);

                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.SWITCH_DEVICES_FAILURE, params);
        }
    },

    /**
     * デバイスの時刻補正値を初期化
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} deviceIds 更新するデバイスのIDの配列
     * @returns {Promise<void>}
     */
    async resetTimeCorrections({ dispatch, commit, rootState }, { deviceIds } = {}) {
        const payload = {
            device_ids: deviceIds,
        };
        try {
            commit(types.RESET_TIME_CORRECTIONS_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'put', url: ApiUrl.MultifactorAuth.RESET_TIME_CORRECTIONS, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.RESET_TIME_CORRECTIONS_SUCCESS, resData);
                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.MultifactorAuth.RESETED);

                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.RESET_TIME_CORRECTIONS_FAILURE, params);
        }
    },

    /**
     * デバイスとユーザーの割り当て情報の解除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} deviceIds 更新するデバイスのIDの配列
     * @returns {Promise<void>}
     */
    async removalUsers({ dispatch, commit, rootState }, { deviceIds } = {}) {
        const payload = {
            device_ids: deviceIds,
        };
        try {
            commit(types.REMOVAL_USERS_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'delete', url: ApiUrl.MultifactorAuth.REMOVAL_USERS, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.REMOVAL_USERS_SUCCESS, resData);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.MultifactorAuth.DEALLOCATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.REMOVAL_USERS_FAILURE, params);
        }
    },

    /**
     * 多要素認証デバイス削除
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {number} deviceIds 削除するデバイスのIDの配列
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, { deviceIds } = {}) {
        const payload = {
            device_ids: deviceIds,
        };
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'delete', url: ApiUrl.MultifactorAuth.DESTROY, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.MultifactorAuth.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} word 検索キーワード
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, { word = '', sort } = {}) {
        const payload = {
            word: word,
            sort: sort,
        };
        try {
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'post', url: ApiUrl.MultifactorAuth.CSV_OUTPUT, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CSV_OUTPUT_FAILURE, params);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
